import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Fragment, useMemo, useState, type MouseEvent } from 'react'
import { chatPanelQuery, chatPreviousQuery, forceChatStatus, type ChatPanel } from '../api/chatSupport'
import { ticketChatQuery } from '../api/easeedesk'
import { Spinner } from '../ui/Spinner'
import { toast } from '../ui/notify'
import { openedCellHtml } from '../ui/DataTable'
import { ChatCannedMessages, ChatChangeTicketButton, ChatMessageList, ChatSendForm, chatClosed, chatStamp, useChatPolling } from './ChatSupport'
import { ticketColumns, type TicketColumn, type TicketRow } from './columns'
import { TicketExpandedRow } from './TicketPage'

/**
 * ECRANUL DE SUPORT PRIN CHAT - ce primeste lista de tichete cand omul are
 * `has_quick_chat_support` (`tickets/index.php`, ramura `chat-tickets-table`):
 *
 *   ChatTicketsTable   `#chatTicketsTable` (`ChatTicketsModel` din easeedesk.js):
 *                      discutiile mele, deasupra listei, fara paginare; clicul
 *                      pe rand deschide discutia in panoul din dreapta
 *   ChatSupportPanel   `.ticket-page-chat > .quick-chat` (`quickChatSupport/{id}`
 *                      + `$.quick_chat_support`): capul cu omul de la client si
 *                      cele doua numaratori, "Details" (discutia de dinainte, pe
 *                      acelasi aparat), firul, casuta, "Add canned messages",
 *                      "Change to <tip>", si, cand `.env` da statusurile,
 *                      "Confirm for closing" (cu categoria) si "Lost"
 *
 * Panoul nu deseneaza recomandarile (`showRecommendations: false`), nici
 * autorul mesajelor (`showAuthor: false`), nici invitatia (`inviteUser: false`)
 * - exact ce spunea `quick_chat_support/index.php`.
 */

/* ------------------------------------------------------------------------ */
/*  Tabelul discutiilor                                                      */
/* ------------------------------------------------------------------------ */

/** Coloanele lui `ChatTicketsModel`, in ordinea lui - un subset al listei. */
const CHAT_TITLES = ['+', 'ID', 'Type', 'Subject', 'Company', 'Office', 'Affected User/Device', 'Priority', 'Category', 'Created by', 'Created', 'Updated', 'Actions', 'Status']

export function chatTicketColumns(): TicketColumn[] {
  const all = ticketColumns()
  return CHAT_TITLES.map((title, index) => {
    const found = all.find((c) => c.title === title)
    if (!found) throw new Error(`chat table: no list column titled ${title}`)
    return { ...found, index, visible: true }
  })
}

export function ChatTicketsTable({ rows, selected, onSelect }: { rows: TicketRow[]; selected: number | null; onSelect(id: number): void }) {
  const columns = useMemo(chatTicketColumns, [])
  const [opened, setOpened] = useState<number | null>(null)

  const onClick = (event: MouseEvent<HTMLTableSectionElement>) => {
    const target = event.target as HTMLElement
    const tr = target.closest<HTMLTableRowElement>('tr[data-ticket]')
    if (!tr) return
    const id = Number(tr.dataset.ticket)
    if (target.closest('center.show_details')) {
      event.preventDefault()
      setOpened((have) => (have === id ? null : id))
      return
    }
    /* Legaturile si butoanele randului isi fac treaba lor; clicul pe restul
       randului alege discutia (`tr[role=row]` din easeedesk.js). */
    if (target.closest('a, button, input, label')) return
    onSelect(id)
  }

  return (
    <table className="table table-striped table-hover table-bordered dataTableM fixedTable mb-0 tk-chat-table" id="chatTicketsTable">
      <thead>
        <tr role="row">
          {columns.map((c) => (
            <th key={c.index} style={c.width ? { width: c.width } : undefined}>
              {c.titleHtml ? <span dangerouslySetInnerHTML={{ __html: c.titleHtml }} /> : c.title}
            </th>
          ))}
        </tr>
      </thead>
      <tbody onClick={onClick}>
        {rows.length === 0 ? (
          <tr>
            <td colSpan={columns.length} className="tk-chat-table-empty">
              No chat conversation is assigned to you.
            </td>
          </tr>
        ) : (
          rows.map((row, i) => {
            const extra = columns
              .map((c) => c.rowClass?.(row))
              .filter((v): v is string => !!v)
              .join(' ')
            const isOpen = opened === row.ID
            return (
              <Fragment key={row.ID}>
                <tr
                  id={`chat_row_${row.ID}`}
                  role="row"
                  data-ticket={row.ID}
                  className={
                    (i % 2 === 0 ? 'odd' : 'even') +
                    (selected === row.ID ? ' ticket-chat-selected' : '') +
                    (isOpen ? ' details parent' : '') +
                    (extra ? ` ${extra}` : '')
                  }
                >
                  {columns.map((c) => (
                    <td key={c.index} style={c.cellStyle?.(row)} dangerouslySetInnerHTML={{ __html: isOpen ? openedCellHtml(c.html(row)) : c.html(row) }} />
                  ))}
                </tr>
                {isOpen && (
                  <tr className="rowDetails">
                    <td className="rowDetails" colSpan={columns.length}>
                      <TicketExpandedRow id={row.ID} />
                    </td>
                  </tr>
                )}
              </Fragment>
            )
          })
        )}
      </tbody>
    </table>
  )
}

/* ------------------------------------------------------------------------ */
/*  Panoul                                                                   */
/* ------------------------------------------------------------------------ */

/** `ChatDetails`: discutia de pe tichetul de chat dinainte, pe acelasi aparat; se cere la prima deschidere. */
function ChatDetails({ id, open }: { id: number; open: boolean }) {
  const previous = useQuery({ ...chatPreviousQuery(id), enabled: open })
  if (!open) return null

  return (
    <div className="quick-chat-details tk-open">
      {!previous.data ? (
        <Spinner />
      ) : previous.data.messages.length === 0 ? (
        <div className="tk-chat-detail-none">No details</div>
      ) : (
        previous.data.messages.map((m) => (
          <div key={m.id}>
            <span className="tk-chat-detail-time">{chatStamp(m.at)}</span>{' '}
            <span className="tk-chat-detail-who" style={{ color: m.kind === 'agent' ? '#0095ff' : '#c03434' }}>
              {m.author}:{' '}
            </span>
            {m.message}
          </div>
        ))
      )}
    </div>
  )
}

/**
 * `ChatChangeStatus`: "Confirm for closing" deschide formularul cu categoria,
 * subcategoria si operatia (`easeedesk/changeStatus/{id}`, `force_status=closing`);
 * "Lost" intreaba si trimite `force_status=lost`. Dupa scriere, tabelul de
 * discutii si panoul se reincarca (`refresh_tickets`).
 */
function ChatStatusActions({ id, panel, onChanged }: { id: number; panel: ChatPanel; onChanged(): void }) {
  const [form, setForm] = useState(false)
  const [category, setCategory] = useState('')
  const [subcategory, setSubcategory] = useState('')
  const [operation, setOperation] = useState('')
  const lists = useQuery({ ...chatPanelQuery(id, Number(category), Number(subcategory)), enabled: category !== '' })

  const change = useMutation({
    mutationFn: (force: 'closing' | 'lost') =>
      forceChatStatus(id, force, { ticket_category: category, ticket_subcategory: subcategory, ticket_operation: operation }),
    onSuccess: () => {
      toast('Ticket status changed!', 'Success', 'success')
      onChanged()
    },
    onError: (err: Error) => toast(err.message || 'Something is wrong! Please try again later!', 'Error', 'error'),
  })

  if (!panel.can_change_status) return null

  return (
    <div className="mt-1 chat-status-actions">
      <hr />
      <p className="font-weight-bold mb-1">Change status</p>
      <div className="chat-status-buttons d-flex">
        <button type="button" className="btn btn-danger" onClick={() => setForm((f) => !f)}>
          <i className="fas fa-archive" aria-hidden="true"></i> Confirm for closing
        </button>
        <button
          type="button"
          className="btn btn-warning"
          disabled={change.isPending}
          onClick={() => {
            if (window.confirm('Are you sure ?')) change.mutate('lost')
          }}
        >
          <i className="far fa-compass" aria-hidden="true"></i> Lost
        </button>
      </div>
      {form && (
        <form
          id="chat-confirm-closing-form"
          className="mt-1"
          method="post"
          onSubmit={(ev) => {
            ev.preventDefault()
            if (window.confirm('Are you sure ?')) change.mutate('closing')
          }}
        >
          <div className="form-group">
            <select
              name="ticket_category"
              id="confirm-closing-categories"
              className="form-control"
              aria-label="Category"
              value={category}
              onChange={(ev) => {
                setCategory(ev.target.value)
                setSubcategory('')
                setOperation('')
              }}
            >
              <option value="">-- Category --</option>
              {panel.categories.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <select
              name="ticket_subcategory"
              id="confirm-closing-subcategories"
              className="form-control"
              aria-label="Subcategory"
              value={subcategory}
              disabled={category === ''}
              onChange={(ev) => {
                setSubcategory(ev.target.value)
                setOperation('')
              }}
            >
              <option value="">-- Subcategory --</option>
              {(lists.data?.subcategories ?? []).map((c) => (
                <option key={c.value} value={c.value}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <select
              name="ticket_operation"
              id="confirm-closing-operations"
              className="form-control"
              aria-label="Operation"
              value={operation}
              disabled={subcategory === ''}
              onChange={(ev) => setOperation(ev.target.value)}
            >
              <option value="">-- Operation --</option>
              {(lists.data?.operations ?? []).map((c) => (
                <option key={c.value} value={c.value}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <input type="hidden" value="closing" name="force_status" />
            <button type="submit" className="btn btn-block btn-primary" disabled={change.isPending}>
              Confirm
            </button>
          </div>
        </form>
      )}
    </div>
  )
}

export function ChatSupportPanel({ id, onRefresh }: { id: number; onRefresh?(): void }) {
  const client = useQueryClient()
  const panel = useQuery(chatPanelQuery(id))
  const chat = useQuery(ticketChatQuery(id))
  const [text, setText] = useState('')
  const [details, setDetails] = useState(false)

  const active = !chatClosed(panel.data)
  useChatPolling(id, active, () => undefined)

  const refresh = () => {
    void client.invalidateQueries({ queryKey: ['easeedesk', 'ticket', id] })
    void client.invalidateQueries({ queryKey: ['easeedesk', 'chat', 'tickets'] })
    onRefresh?.()
  }

  if (panel.error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {panel.error.message}
      </div>
    )
  }
  if (!panel.data || !chat.data) return <Spinner />
  const p = panel.data

  return (
    <div className="quick-chat">
      <div className="chat-header">
        <div className="chat-customer">
          <i className="fas fa-user-circle" aria-hidden="true"></i>
          {p.affected_user !== '' ? p.affected_user : '-'}
        </div>
        <div className="row row-no-gutters chat-counter-cards">
          <div className="col-sm-6">
            Open expert requests
            <div className="chat-counter">{p.expert_requests}</div>
          </div>
          <div className="col-sm-6">
            Chat last 24h
            <div className="chat-counter">{p.chat_last_tickets}</div>
          </div>
        </div>
        <hr className="chat-divider" />
        <div
          className="chat-details"
          role="button"
          tabIndex={0}
          onClick={() => setDetails((d) => !d)}
          onKeyDown={(ev) => {
            if (ev.key === 'Enter') setDetails((d) => !d)
          }}
        >
          Details <i className={details ? 'fas fa-angle-double-up' : 'fas fa-angle-double-down'} aria-hidden="true"></i>
        </div>
      </div>

      <div className="chat-container" data-ticket={id}>
        <div className="messages">
          <ChatMessageList messages={chat.data.messages} showAuthor={false} colors={p.chat_settings} />
        </div>
        {active ? (
          <div className="actions">
            <ChatSendForm id={id} text={text} onText={setText} compact />
            <div className="tk-chat-actions">
              <ChatCannedMessages messages={p.predefined_messages} onPick={(m) => setText((have) => (have ? have + ' ' + m : m))} />
              {p.change_ticket && <ChatChangeTicketButton id={id} change={p.change_ticket} onDone={refresh} />}
            </div>
            <ChatStatusActions id={id} panel={p} onChanged={refresh} />
          </div>
        ) : (
          <div className="actions">
            <div className="alert alert-info">
              <i className="fas fa-info-circle" aria-hidden="true"></i> This conversation is closed!
            </div>
          </div>
        )}
        <ChatDetails id={id} open={details} />
      </div>
    </div>
  )
}
